import type { Editor, TLShapeId } from "@tldraw/tldraw";

export type FoldAxis = "x" | "y";

interface FoldLineInput {
  panelWidthsMm: number[];
  widthMm: number;
  // Canvas length (px) of the axis the panels run along.
  displayLength: number;
  isFront: boolean;
}

// Fold positions in px from the left (or top) edge; the back side is mirrored because the sheet is turned over.
export function computeFoldLines({ panelWidthsMm, widthMm, displayLength, isFront }: FoldLineInput): number[] {
  if (panelWidthsMm.length < 2 || widthMm <= 0) return [];

  let cumulativeMm = 0;
  const front = panelWidthsMm.slice(0, -1).map((panelMm) => {
    cumulativeMm += panelMm;
    return (displayLength * cumulativeMm) / widthMm;
  });

  return isFront ? front : front.map((position) => displayLength - position).reverse();
}

export const FRONT_FOLD_LINE_ID = "shape:frontFoldLine";
export const BACK_FOLD_LINE_ID = "shape:backFoldLine";

interface FoldGeometry {
  panelWidthsMm: number[];
  widthMm: number;
  heightMm: number;
}

interface CanvasShape {
  x: number;
  y: number;
  props: { w: number; h: number };
}

const hasPanels = (panelWidthsMm: number[]): boolean => panelWidthsMm.length > 1;

// Keeps the locked foldLine guides in step with the front/back canvases (call after the base shapes are laid out).
export function syncFoldLines(editor: Editor, geometry: FoldGeometry): void {
  const front = editor.getShape("shape:front" as TLShapeId) as unknown as CanvasShape | undefined;
  const back = editor.getShape("shape:back" as TLShapeId) as unknown as CanvasShape | undefined;
  if (!front || !back) return;

  const sides = [
    { id: FRONT_FOLD_LINE_ID, canvas: front, isFront: true },
    { id: BACK_FOLD_LINE_ID, canvas: back, isFront: false },
  ];

  if (!hasPanels(geometry.panelWidthsMm)) {
    const stale = sides.map((side) => side.id as TLShapeId).filter((id) => editor.getShape(id));
    if (stale.length > 0) editor.run(() => editor.deleteShapes(stale), { ignoreShapeLock: true });
    return;
  }

  // The panels run along the format's width; a canvas turned against the format's own orientation folds along y.
  const canvasIsLandscape = front.props.w > front.props.h;
  const formatIsLandscape = geometry.widthMm > geometry.heightMm;
  const axis: FoldAxis = canvasIsLandscape === formatIsLandscape ? "x" : "y";

  const shapes = sides.map(({ id, canvas, isFront }) => ({
    id: id as TLShapeId,
    type: "foldLine",
    x: canvas.x,
    y: canvas.y,
    isLocked: true,
    props: {
      w: canvas.props.w,
      h: canvas.props.h,
      axis,
      folds: computeFoldLines({
        panelWidthsMm: geometry.panelWidthsMm,
        widthMm: geometry.widthMm,
        displayLength: axis === "x" ? canvas.props.w : canvas.props.h,
        isFront,
      }),
    },
  }));

  editor.run(
    () => {
      const toUpdate = shapes.filter((shape) => editor.getShape(shape.id));
      const toCreate = shapes.filter((shape) => !editor.getShape(shape.id));
      if (toUpdate.length > 0) editor.updateShapes(toUpdate as never);
      if (toCreate.length > 0) editor.createShapes(toCreate as never);
    },
    { ignoreShapeLock: true }
  );
}
